import { queryOptions } from '@tanstack/react-query';
import type { HevitonApiWebLegacy } from '@heviton/heviton-api-web-legacy';
import { parseApiError, STALE_TIME } from '@/shared/api';
import { fromRawPlantOperation } from '@/shared/data-mappers';
import type { PlantOperationSummary } from '@/shared/data-types';

/**
 * 발전소 운영 스냅샷(누적 지표) query 정의 SSOT. BE = **web-legacy(2.0)** `POST /collect/plant/operation/info`.
 *
 * v3(`/plants`)에 누적 발전량이 없어 이것만 2.0 에서 받는다 — admin 발전소 상세 운영현황과 **같은 엔드포인트**라
 * 두 앱의 '총 누적 발전량'이 같은 값을 가리킨다(FE 가 각자 합산하면 어긋난다).
 *
 * wire 주의 — `plant_name` 은 이름이 아니라 **plantId** 를 싣는다(BE 서비스가 이 필드를 식별자로 쓴다,
 * admin `plantOperationInfoQueryOptions` 정합). `energy` 는 필수라 빈값이면 400 → 미확보 시 미발화.
 *
 * 발전소 단건 전용(배치 조회 없음) — 전체 스코프에서 쓰려면 발전소 수만큼 호출해야 하므로 단일 스코프에서만 쓴다.
 */
export const PLANT_OPERATION_QUERY_KEY = ['plant', 'operation'] as const;

export const plantOperationQueryKey = (plantId: string, energy: string) =>
  [...PLANT_OPERATION_QUERY_KEY, plantId, energy] as const;

/** 부재 발전소 업무코드 — 2.0 운영 조회는 HTTP 200 + 이 코드로 "없음"을 알린다(admin 정합). */
const PLANT_ABSENT_CODE = 17;

/** 부재 발전소 폴백 — 전 지표 null(카드는 '-'). 에러 화면 대신 빈 값으로 강등한다. */
const EMPTY_OPERATION: PlantOperationSummary = {
  plantTotalGeneration: null,
  plantTotalAvgHours: null,
  plantOperatingDays: null,
  plantOperatingYears: null,
  plantOperatingMonths: null,
};

export const plantOperationQueryOptions = (
  api: HevitonApiWebLegacy,
  plantId: string | undefined,
  energy: string | undefined,
) =>
  queryOptions<PlantOperationSummary>({
    queryKey: plantOperationQueryKey(plantId ?? '', energy ?? ''),
    queryFn: async ({ signal }) => {
      try {
        return fromRawPlantOperation(
          await api.plant.operationInfo(
            { plant_name: plantId ?? '', energy: energy ?? '' },
            { signal },
          ),
        );
      } catch (error) {
        // 부재만 빈 값으로 강등하고 그 외 에러는 표면화 — 조용한 '-' 로 장애를 숨기지 않는다.
        if (parseApiError(error).code === PLANT_ABSENT_CODE) return EMPTY_OPERATION;
        throw error;
      }
    },
    // 누적은 **전일까지** 기준이라 하루 단위로만 변한다 — 화면 재진입마다 다시 부를 이유가 없다.
    staleTime: STALE_TIME.STATIC,
    // `energy` 는 wire 필수(빈값 400). v3 `/plants` 임베드라 보통 같이 오지만 결측 가능해 가드한다.
    enabled: Boolean(plantId && energy),
  });
